/**
 * Mock 公共工具
 *
 * 任何 API 类均可 import 使用，实现开发阶段的接口 mock。
 * 在 URL 加 ?mock=1 激活，首次激活后存入 sessionStorage，SPA 导航不丢失。
 *
 * 用法：
 * ```ts
 * import { withMock } from '@/fetch/core/mock';
 * import * as Mock from './myModuleMock';
 *
 * public async getList(params: ListParams): Promise<ApiResponse<ListData>> {
 *   return withMock('list', params, () => Mock.mockGetList(params), () =>
 *     request({ path: '/api/list', method: Method.POST, body: params }),
 *   );
 * }
 * ```
 */

import { ApiResponse } from '@/types';
import { HttpStatusCode } from './constant';

const MOCK_SESSION_KEY = '__mock_enabled__';

/** URL 含 ?mock=1 时启用 mock 模式 */
export const isMockEnabled = (): boolean => {
  try {
    if (new URLSearchParams(window.location.search).get('mock') === '1') {
      sessionStorage.setItem(MOCK_SESSION_KEY, '1');
      return true;
    }
    return sessionStorage.getItem(MOCK_SESSION_KEY) === '1';
  } catch {
    return false;
  }
};

/** 模拟异步延迟 */
export const mockDelay = (ms = 200): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/** 构造成功响应 */
export const mockOk = <T>(data: T): ApiResponse<T> => ({
  code: HttpStatusCode.Success as string,
  data,
  message: 'success',
});

/** 构造失败响应 */
export const mockFail = (message: string): ApiResponse<null> => ({
  code: '0409' as string,
  data: null,
  message,
});

/**
 * Mock 包装器：mock 模式下执行 mockFn 并打印控制台日志，否则走真实请求
 *
 * @param label    接口标签（显示在控制台）
 * @param params   请求参数（显示在控制台）
 * @param mockFn   mock 数据生产函数
 * @param requestFn 真实请求函数
 */
export const withMock = async <T>(
  label: string,
  params: any,
  mockFn: () => T,
  requestFn: () => Promise<ApiResponse<T>>,
): Promise<ApiResponse<T>> => {
  if (isMockEnabled()) {
    await mockDelay();
    const data = mockFn();
    const res = mockOk(data);
    console.groupCollapsed(`%c[Mock] ${label}`, 'color: #faad14; font-weight: bold');
    console.log('请求参数:', params);
    console.log('响应数据:', res);
    console.groupEnd();
    return res;
  }
  return requestFn();
};
